import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { PageTitle, Panel, Btn, Sel, NameFields, emptyName, toNamePayload, StatusBadge } from "@/admin/ui";

function ReportDetail({ id }) {
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/admin/reports/${id}`).then((x) => setR(x.data)); }, [id]);
  if (!r) return null;
  const n = r.narrative || {};
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-3" data-testid="report-detail">
      <div className="rounded-md bg-[#FDFBF7] p-8 text-stone-900 lg:col-span-2">
        {r.watermark && <p className="mb-4 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-red-700">{r.watermark}</p>}
        <h2 className="font-serif-display text-4xl">{r.display_name}</h2>
        <p className="mt-4 font-serif-display text-xl italic text-[#5B2C36]">{n.introduction}</p>
        {(n.sections || []).map((s) => (
          <div key={s.domain} className="mt-6">
            <h3 className="font-serif-display text-2xl">{s.title}</h3>
            {(s.paragraphs || []).map((p, i) => <p key={i} className="mt-2 text-sm leading-relaxed">{p}</p>)}
          </div>
        ))}
        {n.integrated && <><h3 className="mt-6 font-serif-display text-2xl">Your Overall Interpretation</h3><p className="mt-2 text-sm leading-relaxed">{n.integrated}</p></>}
        {n.summary && <><h3 className="mt-6 font-serif-display text-2xl">Concluding Thoughts</h3><p className="mt-2 text-sm leading-relaxed">{n.summary}</p></>}
      </div>
      <div className="space-y-4">
        <Panel title="Originality check" testId="similarity-panel">
          {(r.similarity || []).map((s) => (
            <p key={s.domain} className={`text-xs ${s.flag ? "text-red-400" : "text-muted-foreground"}`}>{s.domain}: {(s.overlap_ratio * 100).toFixed(1)}% shared 6-word phrases{s.flag && ` — ${s.shared_phrases.slice(0, 3).join(" / ")}`}</p>
          ))}
        </Panel>
        <Panel title="Source audit" testId="report-source-audit">
          {(r.source_audit || []).map((s) => (
            <div key={s.domain} className="mb-3">
              <p className="text-xs font-semibold">{s.title}</p>
              {s.rules.map((x) => <p key={x.rule_id} className="text-xs text-muted-foreground"><span className="font-mono-ui text-foreground">{x.code}</span> [{x.status}] ← {x.source?.document_title}</p>)}
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}

export default function Reports() {
  const [n, setN] = useState({ ...emptyName, first: "Bharat", no_surname: true });
  const [ruleset, setRuleset] = useState("preview_drafts");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/reports").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const busy = rows.some((r) => r.status === "composing");
  useEffect(() => { if (!busy) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [busy, load]);
  const generate = async () => {
    try { await api.post("/admin/reports", { ...toNamePayload(n), ruleset }); toast.success("Report generation started (about 1–2 minutes)"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageTitle title="Reports" sub="Generate test reports without payment, preview the narrative, check originality against the source passages, and download the A4 PDF." />
      <Panel>
        <NameFields n={n} setN={setN} prefix="report" />
        <div className="mt-3 flex flex-wrap gap-2">
          <Sel value={ruleset} onChange={setRuleset} options={[["preview_drafts", "Preview: draft + approved rules (watermarked)"], ["draft", "Approved rules (unpublished)"], ["active", "Active published version"]]} testId="report-ruleset-select" />
          <Btn onClick={generate} disabled={!n.first.trim()} data-testid="report-generate-btn">Generate report</Btn>
        </div>
      </Panel>
      <Panel title={`Reports (${rows.length})`} className="mt-6" testId="reports-list">
        <ul className="divide-y divide-border text-sm">
          {rows.map((r) => (
            <li key={r.id} className="py-3" data-testid={`report-row-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span><StatusBadge s={r.status === "ready" ? "done" : r.status === "failed" ? "failed" : "running"} /> <strong className="ml-2">{r.display_name}</strong> <span className="text-xs text-muted-foreground">· {r.ref} · {r.ruleset} · {r.finding_count ?? "–"} findings{r.similarity_flags ? ` · ${r.similarity_flags} originality flag(s)` : ""}</span></span>
                <span className="flex gap-2">
                  {r.status === "ready" && <Btn variant="ghost" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`report-view-${r.id}`}>{open === r.id ? "Hide" : "View"}</Btn>}
                  {r.status === "ready" && <Btn variant="ghost" onClick={() => downloadFile(`/admin/reports/${r.id}/pdf`, `${r.ref}.pdf`)} data-testid={`report-pdf-${r.id}`}><FileDown className="h-4 w-4" /> PDF</Btn>}
                </span>
              </div>
              {r.status === "failed" && <p className="mt-1 text-xs text-red-400">{r.error}</p>}
              {open === r.id && <ReportDetail id={r.id} />}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
